import { useCurrentUserProfile } from "./useUserProfile";

// ESS admin screens (categories, custodians, historical import) are
// gated on the account-level admin roles rather than an ESS workflow
// role — they configure the scheme itself, not a suggestion.
const ADMIN_ROLES = ["admin", "superadmin"];

/**
 * Pure role test behind `useIsAdmin`, so the membership rule can be
 * unit-tested without a profile query. `roles` comes from
 * /auth/get_user_profile; it is absent on legacy pre-RBAC responses,
 * which read as "not an admin".
 */
export function hasAdminRole(roles?: string[] | null): boolean {
  return (roles ?? []).some((role) =>
    ADMIN_ROLES.includes(role.trim().toLowerCase()),
  );
}

// The ESS custodian bucket: the roles that own the scheme's paperwork
// rather than one suggestion's review. Names are the yaml's own role
// tokens (see /ess/auth/roles — AdminCustodians maps its two admin keys
// onto "hr" / "corporate_communication"), matched exactly so a workflow
// role can't slip in on a substring.
const CUSTODIAN_ROLES = ["hr", "approver", "corporate_communication"];

/**
 * Pure role test behind `useIsCustodian`. Admins are custodians too —
 * every screen that asks this question also lets an admin through.
 */
export function hasCustodianRole(roles?: string[] | null): boolean {
  if (hasAdminRole(roles)) return true;
  return (roles ?? []).some((role) =>
    CUSTODIAN_ROLES.includes(role.trim().toLowerCase()),
  );
}

/**
 * Whether the signed-in user may reach the admin screens. `isLoading` is
 * true only while the profile is in flight — callers must not treat that
 * as "not an admin" or the Admin nav row would flicker in after mount.
 */
export function useIsAdmin(): { isAdmin: boolean; isLoading: boolean } {
  const { data, isLoading } = useCurrentUserProfile();
  return { isAdmin: hasAdminRole(data?.roles), isLoading };
}

/**
 * Whether the signed-in user acts for the scheme rather than for one
 * suggestion — drives the affordances that bypass per-suggestion
 * ownership (resending a notification, recording implementation for a
 * department that isn't theirs). Server-side rules remain authoritative.
 */
export function useIsCustodian(): { isCustodian: boolean; isLoading: boolean } {
  const { data, isLoading } = useCurrentUserProfile();
  return { isCustodian: hasCustodianRole(data?.roles), isLoading };
}

// The baseline ESS role (decided 2026-09-29): an employee submits and
// follows their own suggestions. On its own it carries no department-wide
// reach server-side, so the scheme-wide Overview is not offered.
export const EMPLOYEE_ROLE = "employee";

/**
 * Pure role test behind `useIsEmployeeOnly`: true when the user holds at
 * least one ESS role and every one of them is `employee`. An empty or
 * missing list is not "employee-only" — RequireAuth already handles a
 * user with no role.
 */
export function isEmployeeOnly(roles?: string[] | null): boolean {
  const normalized = (roles ?? []).map((role) => role.trim().toLowerCase());
  return (
    normalized.length > 0 && normalized.every((role) => role === EMPLOYEE_ROLE)
  );
}

/**
 * Whether the signed-in user's ESS roles are only `employee`. Same
 * `isLoading` contract as `useIsAdmin`: do not read `false` as settled
 * while the profile is still in flight.
 */
export function useIsEmployeeOnly(): {
  isEmployeeOnly: boolean;
  isLoading: boolean;
} {
  const { data, isLoading } = useCurrentUserProfile();
  return { isEmployeeOnly: isEmployeeOnly(data?.roles), isLoading };
}
